import { useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import { GET_PRODUCT_WEEKDAY_REPORT } from '../../graphql/reports-queries'
import { dateRangeToUtc } from '../../lib/format'
import {
  DAY_LABELS,
  EmptyData,
  PLANNING_WEEKS,
  QueryError,
  SectionTitle,
  TREND_WEEKS,
  TableLoading,
  avgLabel,
  heatCellProps,
  isMissingFieldError,
  labelOf,
  lastNFullWeeks,
  parseYmd,
  toYmd,
  weekdayOccurrences,
} from './shared'
import type { OrderSource } from './shared'
import type { DrillTarget } from './ProductDrillPanel'

// ─── Types ────────────────────────────────────────────────────────────────────

interface PeriodRow {
  productId: number
  variantId: number
  productName: string
  variantName: string
  qtySold: number
}

interface DayHourRow {
  productId: number
  variantId: number
  isoDay: number
  hour: number
  qty: number
}

interface WeekdayRow {
  productId: number
  variantId: number
  isoDay: number
  weekStart: string
  qty: number
}

interface ProductPattern {
  key: string
  productId: number
  variantId: number
  productName: string
  variantName: string
  label: string
  /** Total over the whole pattern window — the ranking figure. */
  total: number
  /** qty per iso weekday (index 0 = Mon), summed over the window. */
  perDay: number[]
  /** qty per (iso weekday, hour), summed over the window. */
  perDayHour: Map<string, number>
}

const TOP_N = 15
const TREND_WINDOWS = [4, 8, TREND_WEEKS]
/** Below this the trend reads as flat — weekly kitchen volume is noisy. */
const FLAT_PCT = 10

// ─── Helpers ──────────────────────────────────────────────────────────────────

const mean = (values: number[]): number =>
  values.length === 0 ? 0 : values.reduce((a, b) => a + b, 0) / values.length

/** The Mondays of an inclusive whole-week range, oldest first. */
const mondaysOf = (start: string, end: string): string[] => {
  const last = parseYmd(end)
  const weeks: string[] = []
  for (const d = parseYmd(start); d <= last; d.setDate(d.getDate() + 7)) weeks.push(toYmd(d))
  return weeks
}

// ─── Tab ──────────────────────────────────────────────────────────────────────

const PlanningTab = ({
  channel,
  isoDay,
  onIsoDayChange,
  onOpenDrill,
}: {
  channel: OrderSource | null
  isoDay: number
  onIsoDayChange: (day: number) => void
  onOpenDrill: (drill: DrillTarget) => void
}) => {
  const [showAll, setShowAll] = useState(false)
  const weeks = PLANNING_WEEKS

  // Fixed windows — this view is deliberately NOT live: the running week is a
  // partial week and would drag every average down.
  const patternsRange = lastNFullWeeks(weeks)
  const trendRange = lastNFullWeeks(TREND_WEEKS)
  const source = channel ? { order_source: channel } : {}

  const { data, loading, error } = useQuery<{
    patterns: { byProduct: PeriodRow[]; byDayHourProduct: DayHourRow[] }
    trend: { byWeekdayProduct: WeekdayRow[] }
  }>(GET_PRODUCT_WEEKDAY_REPORT, {
    variables: {
      patterns: { ...dateRangeToUtc(patternsRange.start, patternsRange.end), ...source },
      trend: { ...dateRangeToUtc(trendRange.start, trendRange.end), ...source },
    },
    // The windows end last Sunday, so the data cannot change within a session —
    // a cache hit is always as good as a refetch.
    fetchPolicy: 'cache-first',
  })

  /** Occurrences of each iso weekday in the pattern window (8 in a whole-week window). */
  const occurrences = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) =>
        weekdayOccurrences(patternsRange.start, patternsRange.end, i + 1)
      ),
    [patternsRange.start, patternsRange.end]
  )

  const products = useMemo(() => {
    const map = new Map<string, ProductPattern>()

    for (const r of data?.patterns?.byProduct ?? []) {
      map.set(`${r.productId}|${r.variantId}`, {
        key: `${r.productId}|${r.variantId}`,
        productId: r.productId,
        variantId: r.variantId,
        productName: r.productName,
        variantName: r.variantName,
        label: labelOf(r.productName, r.variantName),
        total: r.qtySold,
        perDay: new Array(7).fill(0),
        perDayHour: new Map(),
      })
    }

    for (const r of data?.patterns?.byDayHourProduct ?? []) {
      const product = map.get(`${r.productId}|${r.variantId}`)
      if (!product) continue
      product.perDay[r.isoDay - 1] += r.qty
      const cellKey = `${r.isoDay}|${r.hour}`
      product.perDayHour.set(cellKey, (product.perDayHour.get(cellKey) ?? 0) + r.qty)
    }

    return [...map.values()].sort((a, b) => b.total - a.total || a.label.localeCompare(b.label))
  }, [data])

  // Weekly series for the selected weekday: sparse rows filled against the full
  // week list, so a zero-sales week counts as 0 instead of vanishing.
  const { kitchenSeries, perProductSeries } = useMemo(() => {
    const weekList = mondaysOf(trendRange.start, trendRange.end)
    const index = new Map(weekList.map((week, i) => [week, i]))

    const kitchen = new Array(weekList.length).fill(0) as number[]
    const perProduct = new Map<string, number[]>()

    for (const r of data?.trend?.byWeekdayProduct ?? []) {
      if (r.isoDay !== isoDay) continue
      const i = index.get(r.weekStart)
      if (i == null) continue
      kitchen[i] += r.qty
      const key = `${r.productId}|${r.variantId}`
      let series = perProduct.get(key)
      if (!series) {
        series = new Array(weekList.length).fill(0)
        perProduct.set(key, series)
      }
      series[i] += r.qty
    }

    return { kitchenSeries: kitchen, perProductSeries: perProduct }
  }, [data, isoDay, trendRange.start, trendRange.end])

  const visible = useMemo(
    () => (showAll ? products : products.slice(0, TOP_N)),
    [products, showAll]
  )

  // Hour axis: the trading day, widened when data falls outside it.
  const hours = useMemo(() => {
    let min = 10
    let max = 21
    for (const product of visible) {
      for (const cellKey of product.perDayHour.keys()) {
        const [day, hour] = cellKey.split('|').map(Number)
        if (day !== isoDay) continue
        if (hour < min) min = hour
        if (hour > max) max = hour
      }
    }
    const list: number[] = []
    for (let h = min; h <= max; h++) list.push(h)
    return list
  }, [visible, isoDay])

  const dayLabel = DAY_LABELS[isoDay - 1]

  return (
    <div className="space-y-6">
      {/* Weekday picker */}
      <div className="flex flex-wrap items-center gap-2">
        {DAY_LABELS.map((label, i) => (
          <button
            key={label}
            type="button"
            onClick={() => onIsoDayChange(i + 1)}
            className={`text-sm px-3 py-1.5 rounded-lg font-medium transition-colors ${
              isoDay === i + 1
                ? 'bg-gray-900 text-white'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
          >
            {label}
          </button>
        ))}
        <span className="text-xs text-gray-400 ml-2">
          {weeks} full weeks · {patternsRange.start} – {patternsRange.end} · units only, staff meals
          included
        </span>
      </div>

      {loading && !data && <TableLoading />}

      {error &&
        (isMissingFieldError(error) ? (
          <QueryError message="Planning patterns are waiting for an API deploy — the weekday and hour rollups (byProduct / byDayHourProduct / byWeekdayProduct) do not exist in the schema yet." />
        ) : (
          <QueryError message={`Failed to load planning patterns: ${error.message}`} />
        ))}

      {!error && data && products.length === 0 && <EmptyData />}

      {!error && data && products.length > 0 && (
        <>
          {/* 1. Whole-kitchen trend for the selected weekday */}
          <div>
            <SectionTitle>Trend · every {dayLabel}</SectionTitle>
            <div className="flex flex-wrap gap-3">
              {TREND_WINDOWS.map((window) => {
                const series = kitchenSeries.slice(-window)
                // floor, not ceil: an odd window's middle week belongs to NEITHER
                // half — counting it in both dilutes the trend.
                const half = Math.floor(window / 2)
                const earliest = mean(series.slice(0, half))
                const recent = mean(series.slice(-half))
                const pct = earliest > 0 ? ((recent - earliest) / earliest) * 100 : null
                const flat = pct == null ? recent === 0 : Math.abs(pct) < FLAT_PCT
                const upOrNew = pct == null || pct > 0
                const tone = flat ? 'text-gray-500' : upOrNew ? 'text-emerald-600' : 'text-red-600'
                return (
                  <div
                    key={window}
                    className="bg-white rounded-xl border border-gray-100 px-4 py-3 min-w-[9rem]"
                  >
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      {window} weeks
                    </p>
                    <p className={`mt-1 text-lg font-bold ${tone}`}>
                      {flat ? '→' : upOrNew ? '↑' : '↓'}{' '}
                      {pct == null
                        ? recent === 0
                          ? '0%'
                          : 'new'
                        : `${pct > 0 ? '+' : ''}${pct.toFixed(0)}%`}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-400">
                      {avgLabel(recent)} vs {avgLabel(earliest)} units
                    </p>
                  </div>
                )
              })}
            </div>
          </div>

          {/* 2. Mon–Sun averages */}
          <div>
            <SectionTitle>Average per day</SectionTitle>
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Product
                      </th>
                      {DAY_LABELS.map((label, i) => (
                        <th
                          key={label}
                          className={`px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide ${
                            isoDay === i + 1 ? 'text-gray-900 bg-gray-100' : 'text-gray-500'
                          }`}
                        >
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {visible.map((product) => {
                      const series = perProductSeries.get(product.key) ?? []
                      const window = series.slice(-weeks)
                      const min = window.length > 0 ? Math.min(...window) : 0
                      const max = window.length > 0 ? Math.max(...window) : 0
                      return (
                        <tr
                          key={product.key}
                          onClick={() =>
                            onOpenDrill({
                              productId: product.productId,
                              variantId: product.variantId,
                              productName: product.productName,
                              variantName: product.variantName,
                              start: patternsRange.start,
                              end: patternsRange.end,
                            })
                          }
                          className="hover:bg-gray-50 cursor-pointer transition-colors"
                        >
                          <td className="px-4 py-2.5 text-gray-900 font-medium truncate max-w-[16rem]">
                            {product.label}
                          </td>
                          {DAY_LABELS.map((label, i) => {
                            const occ = occurrences[i]
                            const avg = occ > 0 ? product.perDay[i] / occ : 0
                            const selected = isoDay === i + 1
                            return (
                              <td
                                key={label}
                                className={`px-4 py-2.5 text-right tabular-nums whitespace-nowrap ${
                                  selected ? 'bg-gray-50 text-gray-900 font-semibold' : 'text-gray-600'
                                }`}
                              >
                                {avgLabel(avg)}
                                {selected && (
                                  <span className="text-xs font-normal text-gray-400 ml-1">
                                    ({min}–{max})
                                  </span>
                                )}
                              </td>
                            )
                          })}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Average units per day over {weeks} full weeks; the {dayLabel} column also shows the
              lowest and highest single {dayLabel} in that window.
            </p>
          </div>

          {/* 3. Products × hours for the selected weekday */}
          <div>
            <SectionTitle>Every {dayLabel} · average per hour</SectionTitle>
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <div className="overflow-x-auto">
                <div
                  className="grid gap-px"
                  style={{ gridTemplateColumns: `12rem repeat(${hours.length}, minmax(2.5rem, 1fr))` }}
                >
                  <div />
                  {hours.map((h) => (
                    <div key={`head-${h}`} className="text-[11px] text-gray-400 text-center">
                      {h}
                    </div>
                  ))}

                  {visible.map((product) => {
                    const occ = occurrences[isoDay - 1]
                    // Heat is scaled per ROW: one bestseller must not flatten
                    // every other product into the same pale shade.
                    let rowMax = 0
                    for (let i = 0; i < hours.length; i++) {
                      const qty = product.perDayHour.get(`${isoDay}|${hours[i]}`) ?? 0
                      if (qty > rowMax) rowMax = qty
                    }

                    return (
                      <div key={product.key} className="contents">
                        <div
                          className="text-xs font-medium text-gray-500 truncate pr-2 flex items-center"
                          title={product.label}
                        >
                          {product.label}
                        </div>
                        {hours.map((h) => {
                          const qty = product.perDayHour.get(`${isoDay}|${h}`) ?? 0
                          const avg = occ > 0 ? qty / occ : 0
                          const cell = heatCellProps(qty, rowMax)
                          return (
                            <div
                              key={`${product.key}-${h}`}
                              className={cell.className}
                              style={cell.style}
                              title={`${product.label} · ${dayLabel} ${h}:00 — ${qty} units over ${occ} ${dayLabel}s`}
                            >
                              {qty === 0 ? '' : avgLabel(avg)}
                            </div>
                          )
                        })}
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>

          {products.length > TOP_N && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              {showAll ? `Show top ${TOP_N}` : `Show all ${products.length} products`}
            </button>
          )}
        </>
      )}
    </div>
  )
}

export default PlanningTab
